// modules/navigation/ui.ts — <app-navigation>
// Tab bar inferior + shell de paneles.

import { bus }       from '../../core/event-bus';
import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';
import type { TabId } from './events';

const TABS: { id: TabId; icon: string; label: string }[] = [
  { id: 'registro',    icon: '➕', label: 'Registro'  },
  { id: 'historial',   icon: '📋', label: 'Historial' },
  { id: 'analisis',    icon: '📊', label: 'Análisis'  },
  { id: 'categorias',  icon: '🏷️', label: 'Cats'      },
  { id: 'config',      icon: '⚙️', label: 'Config'    },
];

export class AppNavigation extends HTMLElement {
  static define() { customElements.define('app-navigation', AppNavigation); }

  connectedCallback() {
    this.render();

    useBus('nav:tab-changed', ({ tab }) => this.activateTab(tab), this);
    useBus('auth:signed-out', () => {
      document.getElementById('app-root')!.innerHTML = '';
      const authEl = document.createElement('app-auth');
      document.getElementById('app-root')!.appendChild(authEl);
    }, this);
  }

  private render() {
    const current = getModule<{ current: () => TabId }>('navigation')?.current() ?? 'registro';
    this.innerHTML = `
      <style>
        :host { display: block; }

        /* ── Paneles ── */
        .panels { max-width: var(--container-sm); margin: 0 auto; padding: var(--space-4); }
        .panel  { display: none; }
        .panel.active { display: block; }

        /* ── Tab bar ── */
        .tab-bar {
          position: fixed; bottom: 0; left: 0; right: 0;
          background: var(--color-surface);
          border-top: 1px solid var(--color-border);
          display: flex; justify-content: center;
          padding-bottom: var(--safe-bottom);
          z-index: var(--z-sticky);
          height: calc(var(--tab-bar-height) + var(--safe-bottom));
        }
        .tab-btn {
          flex: 1; max-width: 100px;
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; gap: 2px;
          background: none; border: none; cursor: pointer;
          color: var(--color-text-muted);
          font-family: var(--font-body); font-size: 0.65rem;
          padding: var(--space-2) 0 var(--space-1);
          transition: color var(--duration-fast);
        }
        .tab-btn.active { color: var(--color-primary); }
        .tab-icon { font-size: 1.25rem; line-height: 1; }
      </style>

      <div class="panels">
        ${TABS.map(t => `
          <section class="panel ${t.id === current ? 'active' : ''}" id="panel-${t.id}">
            <app-panel-${t.id}></app-panel-${t.id}>
          </section>
        `).join('')}
      </div>

      <nav class="tab-bar">
        ${TABS.map(t => `
          <button class="tab-btn ${t.id === current ? 'active' : ''}" data-tab="${t.id}">
            <span class="tab-icon">${t.icon}</span>
            <span>${t.label}</span>
          </button>
        `).join('')}
      </nav>
    `;

    this.querySelectorAll<HTMLButtonElement>('.tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const nav = getModule<{ goTo: (tab: TabId) => void }>('navigation');
        nav?.goTo(btn.dataset.tab as TabId);
      });
    });
  }

  private activateTab(tab: TabId) {
    this.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));
    this.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    this.querySelector(`#panel-${tab}`)?.classList.add('active');
    this.querySelector(`[data-tab="${tab}"]`)?.classList.add('active');
  }
}
